import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from 'src/@/components/ui/dialog';
import { Button } from 'src/@/components/ui/button';
import { ScrollArea, ScrollBar } from 'src/@/components/ui/scroll-area';
import { Textarea } from 'src/@/components/ui/textarea';
import v2_actionAgentsPredictions from 'src/actions/v2/ai/v2_actionAgentsPredictions';
import { Loader2, Trash2, Send, MessageSquare } from 'lucide-react';
import Iconify from 'src/components/Iconify';
import v2_actionPredictionAnalyzeChat from 'src/actions/v2/ai/v2_actionPredictionAnalyzeChat';

export default function ModalAiChat({
  agentId,
  aiAgent,
  open,
  dataSendAi,
  onOpenChange,
  triggerLabel,
  buttonDisabled,
  suggestions = [
    "faça um breve resumo dos atendimentos",
    "houve atendimentos ruins?",
    "quantos atendimentos ?"
  ]
}) {
  const [userInput, setUserInput] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const scrollAreaRef = useRef(null);


  useEffect(() => {
    if (!open) return;
    const savedConversation = localStorage.getItem(`chat_ai_analize_history_${agentId}`);
    if (savedConversation) {
      setConversation(JSON.parse(savedConversation));
    }
  }, [open]);

  useEffect(() => {
    if (conversation.length > 0) {
      localStorage.setItem(`chat_ai_analize_history_${agentId}`, JSON.stringify(conversation));
    }
  }, [conversation]);

  useEffect(() => {
    if (open && scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }, [conversation, open]);

  const simulateTyping = async (content) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
    setIsTyping(false);
    return content;
  };

  const handleClearChat = () => {
    setConversation([]);
    localStorage.setItem(`chat_ai_analize_history_${agentId}`, JSON.stringify([]));
  };

  const handleSubmit = async () => {
    if (!userInput.trim() || isLoading) return;
    try {
      setIsLoading(true);
      const newMessage = { role: 'user', content: userInput };
      const updatedConversation = [...conversation, newMessage];
      setConversation(updatedConversation);
      setUserInput('');
      const data = { scope: { history: updatedConversation, ...dataSendAi } };
      const response = await v2_actionPredictionAnalyzeChat(agentId, data);
      const assistantMessage = response.data.data;
      const processedMessage = await simulateTyping(assistantMessage);
      const aiResponse = { role: 'assistant', content: processedMessage };
      const finalConversation = [...updatedConversation, aiResponse];
      setConversation(finalConversation);
      localStorage.setItem(`chat_ai_analize_history_${agentId}`, JSON.stringify(finalConversation));
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          disabled={buttonDisabled}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
        >
          <Iconify icon="mingcute:ai-fill" width={20} />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden rounded-xl shadow-2xl border-0 bg-white">
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 bg-white">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2 tracking-tight">
                <Iconify icon="mingcute:ai-fill" className="text-[#7C3AED]" width={24} />
                Análise com IA
              </DialogTitle>
              <DialogClose className="opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <Iconify icon="mingcute:close-line" width={20} />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <Iconify icon="material-symbols:info-rounded" className="text-[#7C3AED] mt-0.5" width={18} />
              <p className="text-sm text-gray-500 font-medium leading-tight">
                A IA analisará apenas os dados filtrados atualmente na tela de supervisão.
              </p>
            </div>
          </div>
        </DialogHeader>
        <div className="flex flex-col h-[60vh]">
          {/* Mensagens do chat */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full" viewportRef={scrollAreaRef}>
              <div className="flex flex-col gap-4 p-4">
                {conversation.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`relative px-5 py-4 rounded-2xl max-w-[85%] text-sm leading-relaxed shadow-sm ${msg.role === 'user'
                          ? 'bg-[#7C3AED] text-white rounded-tr-sm'
                          : 'bg-[#F9FAFB] border border-gray-100 text-gray-800 rounded-tl-sm'
                        }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-[#F9FAFB] border border-gray-100 px-5 py-4 rounded-2xl rounded-tl-sm shadow-sm">
                      <div className="flex gap-1.5 items-center">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-.15s]" />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-.3s]" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <ScrollBar />
            </ScrollArea>
          </div>
          {/* Área de sugestões */}
          {suggestions && suggestions.length > 0 && (
            <div className="px-6 py-4 bg-white/50 border-t border-gray-50">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Sugestões rápidas
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setUserInput(s)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-[#7C3AED] hover:text-[#7C3AED] transition-all hover:shadow-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Área de input */}
          <div className="p-6 pt-2 bg-white border-t border-gray-100">
            <div className="flex gap-3 items-end">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearChat}
                className="h-11 w-11 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors shrink-0"
                title="Limpar conversa"
              >
                <Trash2 size={20} />
              </Button>

              <div className="flex-1 relative">
                <Textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Pergunte algo sobre os atendimentos..."
                  className="flex-1 min-h-[44px] max-h-32 py-3 px-4 resize-none rounded-xl bg-[#F9FAFB] border-gray-200 focus:border-[#7C3AED] focus:ring-0 text-gray-900 text-sm shadow-sm transition-all placeholder:text-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={isLoading || !userInput.trim()}
                className={`h-11 w-11 rounded-xl flex-shrink-0 transition-all shadow-md ${!userInput.trim() ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#7C3AED] hover:bg-[#6d28d9] text-white shadow-[#7C3AED]/20 active:scale-95'
                  }`}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>

            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
